import { CalendarEventFormFields } from '@griply/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { ResizableTextArea } from 'shared/components/ui/resizable-text-area';
import { ScheduleDateField } from 'shared/components/ui/schedule-date-field';
import { TimePicker } from 'shared/components/ui/time-picker';
import { useToday } from 'shared/contexts/today';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './event-propose-time-menu.style';

export type EventProposeTimeMenuProps = {
  referenceElement: React.RefObject<HTMLElement>;
  fields: CalendarEventFormFields;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  onSubmit: (start: Date, end: Date, comment: string) => void;
  onClose: () => void;
};

export const EventProposeTimeMenu: React.FC<EventProposeTimeMenuProps> = ({
  referenceElement,
  fields,
  dateFormat,
  timeFormat,
  weekStartsOn,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const [startDate, setStartDate] = useState(fields.startDate);
  const [endDate, setEndDate] = useState(fields.endDate);
  const [startTime, setStartTime] = useState(fields.startTime ?? 0);
  const [endTime, setEndTime] = useState(fields.endTime ?? 0);
  const [comment, setComment] = useState('');

  const onChangeStartDate = (date: Date | null) => date && setStartDate(date);
  const onChangeEndDate = (date: Date | null) => date && setEndDate(date);
  const onChangeStartTime = (time?: number) =>
    time !== undefined && setStartTime(time);
  const onChangeEndTime = (time?: number) =>
    time !== undefined && setEndTime(time);

  const start = new Date(startDate.getTime() + startTime);
  const end = new Date(endDate.getTime() + endTime);
  const isValid = end > start;

  const onConfirm = () => onSubmit(start, end, comment);

  return (
    <PopupMenu referenceElement={referenceElement} onOutside={onClose}>
      <Styled.Container>
        <Styled.Fields>
          <ScheduleDateField
            value={startDate}
            onChange={onChangeStartDate}
            today={today}
            weekStartsOn={weekStartsOn}
            dateFormat={dateFormat}
          />
          <TimePicker
            value={startTime}
            timeFormat={timeFormat}
            onChange={onChangeStartTime}
            showAsFormInput
          />
        </Styled.Fields>

        <Styled.Fields>
          <ScheduleDateField
            value={endDate}
            onChange={onChangeEndDate}
            today={today}
            weekStartsOn={weekStartsOn}
            dateFormat={dateFormat}
            minDate={startDate}
          />
          <TimePicker
            value={endTime}
            timeFormat={timeFormat}
            onChange={onChangeEndTime}
            showAsFormInput
          />
        </Styled.Fields>

        <ResizableTextArea
          value={comment}
          onChange={setComment}
          placeholder={t('forms.event.propose-time.comment-placeholder')}
          fullWidth
        />

        <Styled.Interactions>
          <Button
            variant={Variants.Contained}
            size={Sizes.Small}
            color={Colors.White}
            onClick={onClose}
            type="button"
          >
            {t('general.cancel')}
          </Button>
          <Button
            size={Sizes.Small}
            onClick={onConfirm}
            type="button"
            disabled={!isValid}
          >
            {t('forms.event.propose-time.submit')}
          </Button>
        </Styled.Interactions>
      </Styled.Container>
    </PopupMenu>
  );
};
